import React, { useEffect, useRef, useState } from 'react';
import PageHeader from '../../components/ui/PageHeader';
import { classNames } from 'primereact/utils';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { SucursalService } from '../../services/SucursalService';
import { useAuth } from '../../context/AuthContext';
import ActionToolbar from '../../components/ui/ActionToolbar';
import DialogHeader from '../../components/ui/DialogHeader';
import { UbigeoService } from '../../services/UbigeoService';

const Sucursales = () => {
    const { user } = useAuth();
    const emptySucursal = {
        id: null,
        nombre: '',
        direccion: '',
        departamento: '',
        provincia: '',
        distrito: '',
        telefono: '',
        idEmpresa: user?.idEmpresa || 1};

    const [sucursales, setSucursales] = useState([]);
    const [sucursalDialog, setSucursalDialog] = useState(false);
    const [deleteSucursalDialog, setDeleteSucursalDialog] = useState(false);
    const [sucursal, setSucursal] = useState(emptySucursal);
    const [selectedSucursales, setSelectedSucursales] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const toast = useRef(null);
    const dt = useRef(null);

    // Estados para Ubigeos progresivos
    const [departamentos, setDepartamentos] = useState([]);
    const [provinciasDisponibles, setProvinciasDisponibles] = useState([]);
    const [distritosDisponibles, setDistritosDisponibles] = useState([]);

    useEffect(() => {
        loadData();
        // Cargar departamentos al montar el componente
        UbigeoService.getDepartamentos().then(data => setDepartamentos(data.map(d => ({ label: d, value: d }))));
    }, []);

    const loadData = async () => {
        try {
            const response = await SucursalService.getAll();
            setSucursales(response?.data || []);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las sucursales', life: 3000 });
        }
    };

    const openNew = () => {
        setSucursal({ ...emptySucursal, idEmpresa: user?.idEmpresa || emptySucursal.idEmpresa });
        setSubmitted(false);
        // Resetear ubiceos al abrir nuevo registro
        setProvinciasDisponibles([]);
        setDistritosDisponibles([]);
        setSucursalDialog(true);
    };

    // Manejador para cambio de Departamento
    const handleDepartamentoChange = (e) => {
        const deptSelected = e.value;
        setSucursal(prev => ({
            ...prev,
            departamento: deptSelected,
            provincia: '', // Resetear provincia
            distrito: ''   // Resetear distrito
        }));

        // Obtener provincias del departamento seleccionado
        if (deptSelected) {
            UbigeoService.getProvincias(deptSelected).then(data => {
                setProvinciasDisponibles(data.map(p => ({ label: p, value: p })));
            });
            setDistritosDisponibles([]); // Limpiar distritos
        } else {
            setProvinciasDisponibles([]);
            setDistritosDisponibles([]);
        }
    };

    // Manejador para cambio de Provincia
    const handleProvinciaChange = (e) => {
        const provSelected = e.value;
        setSucursal(prev => ({
            ...prev,
            provincia: provSelected,
            distrito: '' // Resetear distrito
        }));

        // Obtener distritos de la provincia seleccionada
        if (provSelected) {
            UbigeoService.getDistritos(sucursal.departamento, provSelected).then(data => {
                setDistritosDisponibles(data.map(d => ({ label: d, value: d })));
            });
        } else {
            setDistritosDisponibles([]);
        }
    };

    // Manejador para cambio de Distrito
    const handleDistritoChange = (e) => {
        setSucursal(prev => ({
            ...prev,
            distrito: e.value
        }));
    };

    const hideDialog = () => {
        setSubmitted(false);
        setSucursalDialog(false);
    };

    const hideDeleteSucursalDialog = () => {
        setDeleteSucursalDialog(false);
    };

    const saveSucursal = async () => {
        setSubmitted(true);

        if (!sucursal.nombre?.trim() || !sucursal.departamento || !sucursal.provincia || !sucursal.distrito) {
            return;
        }

        try {
            const payload = {
                ...sucursal,
                idEmpresa: sucursal.idEmpresa || user?.idEmpresa || 1};

            if (sucursal.id) {
                await SucursalService.update(sucursal.id, payload);
                toast.current.show({ severity: 'success', summary: 'Exito', detail: 'Sucursal actualizada', life: 3000 });
            } else {
                await SucursalService.create(payload);
                toast.current.show({ severity: 'success', summary: 'Exito', detail: 'Sucursal creada', life: 3000 });
            }

            setSucursalDialog(false);
            setSucursal(emptySucursal);
            loadData();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar la sucursal', life: 3000 });
        }
    };

    const editSucursal = (rowData) => {
        setSucursal({
            ...emptySucursal,
            ...rowData});
        
        // Cargar provincias si hay departamento
        if (rowData.departamento) {
            UbigeoService.getProvincias(rowData.departamento).then(data => {
                setProvinciasDisponibles(data.map(p => ({ label: p, value: p })));
                if (rowData.provincia) {
                    UbigeoService.getDistritos(rowData.departamento, rowData.provincia).then(dists => {
                        setDistritosDisponibles(dists.map(d => ({ label: d, value: d })));
                    });
                } else {
                    setDistritosDisponibles([]);
                }
            });
        } else {
            setProvinciasDisponibles([]);
            setDistritosDisponibles([]);
        }

        setSucursalDialog(true);
    };

    const confirmDeleteSucursal = (rowData) => {
        setSucursal(rowData);
        setDeleteSucursalDialog(true);
    };

    const deleteSucursal = async () => {
        try {
            await SucursalService.delete(sucursal.id);
            setDeleteSucursalDialog(false);
            setSucursal(emptySucursal);
            toast.current.show({ severity: 'success', summary: 'Exito', detail: 'Sucursal eliminada', life: 3000 });
            loadData();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar la sucursal', life: 3000 });
        }
    };

    const onInputChange = (e, name) => {
        const value = (e.target && e.target.value) || '';
        setSucursal((prev) => ({ ...prev, [name]: value }));
    };


    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => editSucursal(rowData)} />
                <Button icon="pi pi-trash" rounded outlined onClick={() => confirmDeleteSucursal(rowData)} />
            </React.Fragment>
        );
    };

    



    const sucursalDialogFooter = (
        <div className="dialog-footer-buttons">
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={hideDialog} />
            <Button label="Guardar" icon="pi pi-check" onClick={saveSucursal} autoFocus />
        </div>
    );

    const deleteSucursalDialogFooter = (
        <React.Fragment>
            <Button label="No" icon="pi pi-times" outlined onClick={hideDeleteSucursalDialog} />
            <Button label="Si, Eliminar" icon="pi pi-check" onClick={deleteSucursal} />
        </React.Fragment>
    );

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <PageHeader 
                    title="Gestión de Sucursales" 
                    description="Administre las sucursales y puntos de venta de la empresa." 
                    icon="pi pi-building" 
                />
                <div className="card shadow-2 border-round-xl">
                    <Toast ref={toast} />

                    <ActionToolbar
                        onNew={openNew}
                        newLabel="Nueva Sucursal"
                        onSearch={(val) => setGlobalFilter(val)}
                        searchValue={globalFilter}
                        searchPlaceholder="Buscar sucursal..."
                        extraActions={null}
                    />

                    <DataTable
                        ref={dt}
                        value={sucursales}
                        selection={selectedSucursales}
                        onSelectionChange={(e) => setSelectedSucursales(e.value)}
                        dataKey="id"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        className="datatable-responsive p-datatable-sm"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} sucursales"
                        globalFilter={globalFilter}
                        emptyMessage="No se encontraron sucursales."
                        responsiveLayout="scroll"
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: '4rem' }} />
                        <Column field="nombre" header="Nombre" sortable headerStyle={{ minWidth: '14rem' }} />
                        
                        <Column field="telefono" header="Telefono" sortable headerStyle={{ minWidth: '10rem' }} />
                        <Column field="departamento" header="Departamento" sortable headerStyle={{ minWidth: '10rem' }} />
                        <Column field="provincia" header="Provincia" sortable headerStyle={{ minWidth: '10rem' }} />
                        <Column field="distrito" header="Distrito" sortable headerStyle={{ minWidth: '10rem' }} />
                        
                        <Column body={actionBodyTemplate} headerStyle={{ minWidth: '10rem' }} />
                    </DataTable>

                    <Dialog
                        visible={sucursalDialog}
                        style={{ width: '800px', maxWidth: '95vw' }}
                        header={
                            <DialogHeader
                                title={sucursal.id ? 'Editar Sucursal' : 'Nueva Sucursal'}
                                subtitle={sucursal.id ? 'Modificar datos de la sucursal' : 'Registrar nueva sucursal'}
                                icon="pi pi-building"
                            />
                        }
                        modal
                        className="p-fluid custom-profile-dialog"
                        footer={sucursalDialogFooter}
                        onHide={hideDialog}
                    >
                        <div className="formgrid grid mt-4 dialog-content-specific">
                            <div className="field col-12 md:col-6">
                                <label htmlFor="nombre">Nombre</label>
                                <InputText
                                    id="nombre"
                                    value={sucursal.nombre}
                                    onChange={(e) => onInputChange(e, 'nombre')}
                                    required
                                    autoFocus
                                    className={classNames({ 'p-invalid': submitted && !sucursal.nombre })}
                                />
                                {submitted && !sucursal.nombre && <small className="p-error">El nombre es requerido.</small>}
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="direccion">Dirección</label>
                                <InputText id="direccion" value={sucursal.direccion || ''} onChange={(e) => onInputChange(e, 'direccion')} />
                            </div>

                        
                            <div className="field col-12 md:col-4">
                                <label htmlFor="departamento">Departamento</label>
                                <Dropdown
                                    id="departamento"
                                    value={sucursal.departamento}
                                    options={departamentos}
                                    onChange={handleDepartamentoChange}
                                    placeholder="Seleccionar Departamento"
                                    className={classNames({ 'p-invalid': submitted && !sucursal.departamento })}
                                />
                                {submitted && !sucursal.departamento && <small className="p-error">Departamento requerido.</small>}
                            </div>
                            
                            <div className="field col-12 md:col-4">
                                <label htmlFor="provincia">Provincia</label>
                                <Dropdown
                                    id="provincia"
                                    value={sucursal.provincia}
                                    options={provinciasDisponibles}
                                    onChange={handleProvinciaChange}
                                    placeholder="Seleccionar Provincia"
                                    disabled={!sucursal.departamento}
                                    className={classNames({ 'p-invalid': submitted && !sucursal.provincia })}
                                />
                                {submitted && !sucursal.provincia && <small className="p-error">Provincia requerida.</small>}
                            </div>
                            
                            <div className="field col-12 md:col-4">
                                <label htmlFor="distrito">Distrito</label>
                                <Dropdown
                                    id="distrito"
                                    value={sucursal.distrito}
                                    options={distritosDisponibles}
                                    onChange={handleDistritoChange}
                                    placeholder="Seleccionar Distrito"
                                    disabled={!sucursal.provincia}
                                    className={classNames({ 'p-invalid': submitted && !sucursal.distrito })}
                                />
                                {submitted && !sucursal.distrito && <small className="p-error">Distrito requerido.</small>}
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="telefono">Teléfono</label>
                                <InputText id="telefono" value={sucursal.telefono || ''} onChange={(e) => onInputChange(e, 'telefono')} />
                            </div>
                        </div>
                    </Dialog>

                    <Dialog visible={deleteSucursalDialog} style={{ width: '450px' }} header="Confirmar" modal footer={deleteSucursalDialogFooter} onHide={hideDeleteSucursalDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {sucursal && <span>Esta seguro de eliminar la sucursal <b>{sucursal.nombre}</b>?</span>}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Sucursales;
